(function () {

    const INIT_FLAG = "mbCarouselInit"; // dataset flag name

    const SELECTORS = {
        root: "[data-mb-carousel]",
        viewport: "[data-mb-carousel-viewport]",
        track: "[data-mb-carousel-track]",
        item: "[data-mb-carousel-item]",
        prev: "[data-mb-carousel-prev]",
        next: "[data-mb-carousel-next]",
    };

    function clamp(n, min, max) {
        return Math.max(min, Math.min(max, n));
    }

    function parseNumber(value, fallback) {
        const n = Number(value);
        return Number.isFinite(n) ? n : fallback;
    }

    function getScrollMax(el) {
        return Math.max(0, el.scrollWidth - el.clientWidth);
    }

    function getGapPx(el) {
        const cs = window.getComputedStyle(el);
        const gap = cs.columnGap || cs.gap || "0px";
        const n = parseFloat(gap);
        return Number.isFinite(n) ? n : 0;
    }

    function getItemStepPx(root, viewport, track) {
        const item = root.querySelector(SELECTORS.item);
        if (!item) return Math.round(viewport.clientWidth * 0.8);

        const itemRect = item.getBoundingClientRect();
        const gap = getGapPx(track || item.parentElement || viewport);
        return Math.round(itemRect.width + gap);
    }

    function getConfig(root) {
        const mode = (root.getAttribute("data-mb-carousel-mode") || "percent").toLowerCase();
        const step = parseNumber(root.getAttribute("data-mb-carousel-step"), mode === "item" ? 1 : 0.8);
        const snap = (root.getAttribute("data-mb-carousel-snap") || "false").toLowerCase() === "true";
        const behavior = (root.getAttribute("data-mb-carousel-behavior") || "smooth").toLowerCase();
        const scrollBehavior = behavior === "auto" ? "auto" : "smooth";
        return { mode, step, snap, scrollBehavior };
    }

    function setBtnDisabled(btn, isDisabled) {
        if (!btn) return;
        btn.disabled = !!isDisabled;
        btn.setAttribute("aria-disabled", isDisabled ? "true" : "false");
        btn.tabIndex = isDisabled ? -1 : 0;
    }

    // ✅ accepts arrays
    function updateControls(viewport, prevBtns, nextBtns) {
        const max = getScrollMax(viewport);
        const x = viewport.scrollLeft;

        const eps = 1;
        const disablePrev = x <= eps;
        const disableNext = x >= max - eps;

        prevBtns.forEach((b) => setBtnDisabled(b, disablePrev));
        nextBtns.forEach((b) => setBtnDisabled(b, disableNext));
    }

    function snapToNearest(viewport, stepPx) {
        if (stepPx <= 0) return;

        const max = getScrollMax(viewport);
        const currentX = viewport.scrollLeft;

        // 1. If we are within 10px of the end, don't snap back; stay at the end.
        if (currentX >= max - 10) {
            viewport.scrollTo({ left: max, behavior: "smooth" });
            return;
        }

        // 2. Improved rounding logic
        const target = clamp(Math.round(currentX / stepPx) * stepPx, 0, max);

        // Only trigger a scroll if the difference is significant (prevents infinite loop/jitter)
        if (Math.abs(target - currentX) > 1) {
            viewport.scrollTo({ left: target, behavior: "smooth" });
        }
    }

    function initCarousel(root) {
        if (!root || root.__mbCarousel) return;

        if (root.dataset[INIT_FLAG] === "1") return;
        root.dataset[INIT_FLAG] = "1";



        const viewport =
            root.querySelector(SELECTORS.viewport) ||
            root.querySelector(SELECTORS.track) ||
            root;

        const track = root.querySelector(SELECTORS.track);

        // ✅ multiple controls
        const prevBtns = Array.from(root.querySelectorAll(SELECTORS.prev));
        const nextBtns = Array.from(root.querySelectorAll(SELECTORS.next));

        const cfg = getConfig(root);

        let lastStepPx = 0;

        function computeStepPx() {
            if (cfg.mode === "item") {
                const px = getItemStepPx(root, viewport, track);
                lastStepPx = px > 0 ? px : Math.round(viewport.clientWidth * 0.8);
                return lastStepPx * Math.max(1, Math.round(cfg.step));
            }
            lastStepPx = Math.round(viewport.clientWidth * clamp(cfg.step, 0.05, 2));
            return lastStepPx;
        }

        function scrollByDir(dir) {
            const delta = computeStepPx() * dir;
            const max = getScrollMax(viewport);
            const target = clamp(viewport.scrollLeft + delta, 0, max);
            viewport.scrollTo({ left: target, behavior: cfg.scrollBehavior });
        }

        function onPrevClick(e) {
            e.preventDefault();
            scrollByDir(-1);
        }
        function onNextClick(e) {
            e.preventDefault();
            scrollByDir(1);
        }

        // ✅ if ANY button uses aria-controls, give the root an id if missing
        const hasAriaControls =
            prevBtns.some((b) => b.hasAttribute("aria-controls")) ||
            nextBtns.some((b) => b.hasAttribute("aria-controls"));

        if (hasAriaControls && !root.id) {
            root.id = `mb-carousel-${Math.random().toString(36).slice(2, 9)}`;
        }

        // ✅ if aria-controls attribute exists but is empty, fill it for each button
        if (root.id && hasAriaControls) {
            [...prevBtns, ...nextBtns].forEach((btn) => {
                if (btn.getAttribute("aria-controls") === "") {
                    btn.setAttribute("aria-controls", root.id);
                }
            });
        }

        // ✅ bind all buttons
        prevBtns.forEach((b) => b.addEventListener("click", onPrevClick));
        nextBtns.forEach((b) => b.addEventListener("click", onNextClick));

        const onScroll = () => updateControls(viewport, prevBtns, nextBtns);
        viewport.addEventListener("scroll", onScroll, { passive: true });

        let snapT = null;
        let isScrolling;
        const onScrollForSnap = () => {
            if (!cfg.snap) return;

            // Clear timeout while scrolling
            window.clearTimeout(isScrolling);

            // Set a timeout to run after scrolling stops
            isScrolling = setTimeout(() => {
                const stepPx = cfg.mode === "item" ? getItemStepPx(root, viewport, track) : lastStepPx;
                snapToNearest(viewport, stepPx || computeStepPx());
            }, 150); // Increased slightly for better mobile "momentum" feel
        };
        viewport.addEventListener("scroll", onScrollForSnap, { passive: true });

        const ro = new ResizeObserver(() => {
            updateControls(viewport, prevBtns, nextBtns);
        });
        ro.observe(viewport);

        // Initial state
        updateControls(viewport, prevBtns, nextBtns);

        root.__mbCarousel = {
            destroy() {
                prevBtns.forEach((b) => b.removeEventListener("click", onPrevClick));
                nextBtns.forEach((b) => b.removeEventListener("click", onNextClick));
                viewport.removeEventListener("scroll", onScroll);
                viewport.removeEventListener("scroll", onScrollForSnap);
                ro.disconnect();
                delete root.__mbCarousel;
            },
            scrollNext() {
                scrollByDir(1);
            },
            scrollPrev() {
                scrollByDir(-1);
            },
        };
    }

    function initAll(root) {
        root.querySelectorAll(SELECTORS.root).forEach(initCarousel);
    }

    document.addEventListener("DOMContentLoaded", () => initAll(document));
    document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();
